import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import PayoutAnalytics from '../components/PayoutAnalytics';
import PayoutManagement from '../components/PayoutManagement';
import AdvancedAnalytics from '../components/AdvancedAnalytics';
import EditFormDialog from '../components/EditFormDialog';
import AdminManagement from '../components/AdminManagement';
import ServiceManagement from '../components/ServiceManagement';
import SpecialMenusManagement from '../components/SpecialMenusManagement';
import AdminAlertsPanel from '../components/AdminAlertsPanel';
import DispatchPanel from '../components/DispatchPanel';
import PromotionsManagement from '../components/PromotionsManagement';
import CustomerDeliveryAnalytics from '../components/CustomerDeliveryAnalytics';
import CustomersManager from '../components/CustomersManager';
import ReceiptsManager from '../components/ReceiptsManager';
import RestaurantZonesManager from '../components/RestaurantZonesManager';
import OrderDetailsModal from '../components/OrderDetailsModal';
import RentalDetailsModal from '../components/RentalDetailsModal';
import { RestaurantManagersTab } from '../components/RestaurantManagersTab';
import { PlatformConfigTab } from '../components/PlatformConfigTab';
import { RestaurantHoursEditor } from '../components/RestaurantHoursEditor';
import { 
  LayoutDashboard, Package, Utensils, Truck, Settings,
  TrendingUp, Clock, DollarSign, Plus, ChevronRight,
  Edit, Trash2, Save, Wind, Snowflake, ArrowLeft, Home,
  MapPin, Phone, X, Eye, Grid, Users, BarChart3, UserCog,
  Bell, Tag, Zap, Store, Monitor, Bike, Receipt
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [deleteOrderTarget, setDeleteOrderTarget] = useState(null);
  const [deleteExclude, setDeleteExclude] = useState(false);
  const [rentals, setRentals] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [shishas, setShishas] = useState([]);
  const [flavors, setFlavors] = useState([]);
  const [iceProducts, setIceProducts] = useState([]);
  const [shishaExtras, setShishaExtras] = useState([]);
  const [selectedRental, setSelectedRental] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  // Edit dialogs
  const [editDialog, setEditDialog] = useState({ open: false, type: '', data: null });
  const [hoursDialog, setHoursDialog] = useState({ open: false, restaurant: null });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [statsRes, ordersRes, rentalsRes, restaurantsRes, driversRes, settingsRes, categoriesRes, zonesRes, shishasRes, flavorsRes, iceRes, shishaExtrasRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers }),
        fetch(`${API_URL}/api/orders`, { headers }),
        fetch(`${API_URL}/api/shisha-rentals`, { headers }),
        fetch(`${API_URL}/api/restaurants`),
        fetch(`${API_URL}/api/admin/drivers`, { headers }),
        fetch(`${API_URL}/api/settings`),
        fetch(`${API_URL}/api/service-categories`),
        fetch(`${API_URL}/api/delivery-zones`),
        fetch(`${API_URL}/api/shishas`),
        fetch(`${API_URL}/api/shisha-flavors`),
        fetch(`${API_URL}/api/ice-products`),
        fetch(`${API_URL}/api/admin/shisha-extras`, { headers })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (ordersRes.ok) setOrders(await ordersRes.json());
      if (rentalsRes.ok) setRentals(await rentalsRes.json());
      if (restaurantsRes.ok) setRestaurants(await restaurantsRes.json());
      if (driversRes.ok) setDrivers(await driversRes.json());
      if (settingsRes.ok) setSettings(await settingsRes.json());
      if (categoriesRes.ok) setServiceCategories(await categoriesRes.json());
      if (zonesRes.ok) setDeliveryZones(await zonesRes.json());
      if (shishasRes.ok) setShishas(await shishasRes.json());
      if (flavorsRes.ok) setFlavors(await flavorsRes.json());
      if (iceRes.ok) setIceProducts(await iceRes.json());
      if (shishaExtrasRes.ok) setShishaExtras(await shishaExtrasRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();

    // WebSocket for real-time updates (with auto-reconnect)
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    let ws = null;
    let closed = false;
    const connect = () => {
      if (closed) return;
      ws = new WebSocket(`${wsUrl}/api/ws/admin`);
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_order') {
          setOrders(prev => [data.order, ...prev.filter(o => o.order_id !== data.order.order_id)]);
          toast.info(language === 'pt' ? 'Novo pedido recebido!' : 'New order received!');
        } else if (data.type === 'order_update' && data.order) {
          setOrders(prev => prev.map(o => o.order_id === data.order.order_id ? data.order : o));
        } else if (data.type === 'new_rental') {
          setRentals(prev => [data.rental, ...prev.filter(r => r.rental_id !== data.rental.rental_id)]);
          toast.info(language === 'pt' ? 'Novo aluguer de shisha!' : 'New shisha rental!');
        } else if (data.type === 'rental_update' && data.rental) {
          setRentals(prev => prev.map(r => r.rental_id === data.rental.rental_id ? data.rental : r));
        }
      };
      ws.onclose = () => { if (!closed) setTimeout(connect, 3000); };
    };
    connect();

    // Polling fallback — keeps orders/rentals fresh even if the WS drops
    const headers = { 'Authorization': `Bearer ${token}` };
    const poll = setInterval(async () => {
      try {
        const [oR, rR] = await Promise.all([
          fetch(`${API_URL}/api/orders`, { headers }),
          fetch(`${API_URL}/api/shisha-rentals`, { headers }),
        ]);
        if (oR.ok) setOrders(await oR.json());
        if (rR.ok) setRentals(await rR.json());
      } catch (e) { /* offline — try again next tick */ }
    }, 20000);

    return () => { closed = true; if (ws) ws.close(); clearInterval(poll); };
  }, [language, fetchData, token]);

  const fetchMenuItems = async (restaurantId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/restaurants/${restaurantId}/menu`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMenuItems(data);
      }
    } catch (error) {
      console.error('Error fetching menu:', error);
    }
  };

  const updateOrderStatus = async (orderId, status, driverId = null) => {
    try {
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const body = { status };
      if (driverId) body.driver_id = driverId;

      const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(prev => prev.map(o => o.order_id === orderId ? data.order : o));
        toast.success(language === 'pt' ? 'Status atualizado!' : 'Status updated!');
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao atualizar' : 'Update failed');
    }
  };

  const handleDeleteOrder = async () => {
    if (!deleteOrderTarget) return;
    const orderId = deleteOrderTarget.order_id;
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/${orderId}?exclude_from_reports=${deleteExclude}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setOrders(prev => prev.filter(o => o.order_id !== orderId));
        toast.success(language === 'pt'
          ? (deleteExclude ? 'Pedido eliminado e removido dos relatórios' : 'Pedido eliminado (continua nos relatórios)')
          : 'Order deleted');
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || (language === 'pt' ? 'Erro ao eliminar' : 'Delete failed'));
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao eliminar' : 'Delete failed');
    } finally {
      setDeleteOrderTarget(null);
      setDeleteExclude(false);
    }
  };

  const updateSettings = async () => {
    try {
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success(language === 'pt' ? 'Configurações salvas!' : 'Settings saved!');
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao salvar' : 'Save failed');
    }
  };

  // CRUD Operations
  const handleCreate = async (type, data) => {
    try {
      const endpoints = {
        restaurant: '/api/admin/restaurants',
        menuItem: '/api/admin/menu-items',
        driver: '/api/admin/drivers',
        shisha: '/api/admin/shishas',
        flavor: '/api/admin/shisha-flavors',
        shisha_extra: '/api/admin/shisha-extras',
        zone: '/api/admin/delivery-zones',
        category: '/api/admin/service-categories',
        ice: '/api/admin/ice-products'
      };

      const response = await fetch(`${API_URL}${endpoints[type]}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        toast.success(language === 'pt' ? 'Criado com sucesso!' : 'Created successfully!');
        fetchData();
        if (type === 'menuItem' && selectedRestaurant) {
          fetchMenuItems(selectedRestaurant.restaurant_id);
        }
        setEditDialog({ open: false, type: '', data: null });
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao criar' : 'Create failed');
    }
  };

  const handleUpdate = async (type, id, data) => {
    try {
      const endpoints = {
        restaurant: `/api/admin/restaurants/${id}`,
        menuItem: `/api/admin/menu-items/${id}`,
        driver: `/api/admin/drivers/${id}`,
        shisha: `/api/admin/shishas/${id}`,
        flavor: `/api/admin/shisha-flavors/${id}`,
        shisha_extra: `/api/admin/shisha-extras/${id}`,
        zone: `/api/admin/delivery-zones/${id}`,
        category: `/api/admin/service-categories/${id}`,
        ice: `/api/admin/ice-products/${id}`
      };

      const response = await fetch(`${API_URL}${endpoints[type]}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        toast.success(language === 'pt' ? 'Atualizado!' : 'Updated!');
        fetchData();
        if (type === 'menuItem' && selectedRestaurant) {
          fetchMenuItems(selectedRestaurant.restaurant_id);
        }
        setEditDialog({ open: false, type: '', data: null });
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao atualizar' : 'Update failed');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(language === 'pt' ? 'Tem certeza?' : 'Are you sure?')) return;

    try {
      const endpoints = {
        restaurant: `/api/admin/restaurants/${id}`,
        menuItem: `/api/admin/menu-items/${id}`,
        driver: `/api/admin/drivers/${id}`,
        shisha: `/api/admin/shishas/${id}`,
        flavor: `/api/admin/shisha-flavors/${id}`,
        shisha_extra: `/api/admin/shisha-extras/${id}`,
        zone: `/api/admin/delivery-zones/${id}`,
        category: `/api/admin/service-categories/${id}`,
        ice: `/api/admin/ice-products/${id}`
      };

      const response = await fetch(`${API_URL}${endpoints[type]}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(language === 'pt' ? 'Excluído!' : 'Deleted!');
        fetchData();
        if (type === 'menuItem' && selectedRestaurant) {
          fetchMenuItems(selectedRestaurant.restaurant_id);
        }
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao excluir' : 'Delete failed');
    }
  };

  const statusOptions = [
    { value: 'received', label: language === 'pt' ? 'Recebido' : 'Received' },
    { value: 'preparing', label: language === 'pt' ? 'Preparando' : 'Preparing' },
    { value: 'ready', label: language === 'pt' ? 'Pronto' : 'Ready' },
    { value: 'out_for_delivery', label: language === 'pt' ? 'Em Entrega' : 'Out for Delivery' },
    { value: 'delivered', label: language === 'pt' ? 'Entregue' : 'Delivered' }
  ];

  const getStatusLabel = (status) => {
    const map = language === 'pt' ? {
      pending_payment: 'Aguarda Pagamento', received: 'Recebido', preparing: 'Preparando',
      ready: 'Pronto', out_for_delivery: 'Em Entrega', delivered: 'Entregue', cancelled: 'Cancelado'
    } : {
      pending_payment: 'Awaiting Payment', received: 'Received', preparing: 'Preparing',
      ready: 'Ready', out_for_delivery: 'Out for Delivery', delivered: 'Delivered', cancelled: 'Cancelled'
    };
    return map[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending_payment: 'bg-gray-100 text-gray-600',
      received: 'bg-yellow-100 text-yellow-800',
      preparing: 'bg-blue-100 text-blue-800',
      ready: 'bg-purple-100 text-purple-800',
      out_for_delivery: 'bg-orange-100 text-orange-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Form field configurations per type
  const getFormFields = (type) => {
    switch (type) {
      case 'restaurant':
        return [
          { key: 'name', label: 'Nome', type: 'text', required: true },
          { key: 'description', label: 'Descrição', type: 'textarea' },
          { key: 'cuisine_type', label: 'Tipo de Cozinha', type: 'text' },
          { key: 'logo', label: 'Logo', type: 'file' },
          { key: 'cover_image', label: 'Imagem de Capa', type: 'file' },
          { key: 'delivery_time', label: 'Tempo de Entrega', type: 'text', placeholder: '30-45 min' },
          { key: 'delivery_fee', label: 'Taxa de Entrega (€)', type: 'number', step: '0.50' },
          { key: 'min_order', label: 'Pedido Mínimo (€)', type: 'number', step: '0.50' },
          { key: 'address', label: 'Endereço', type: 'text' },
          { key: 'phone', label: 'Telefone', type: 'text' },
          { key: 'opening_hours', label: language === 'pt' ? 'Horário (Seg-Sex)' : 'Hours (Mon-Fri)', type: 'text', placeholder: '09:00-23:00' },
          { key: 'opening_hours_saturday', label: language === 'pt' ? 'Horário (Sábado)' : 'Hours (Saturday)', type: 'text', placeholder: '10:00-23:00' },
          { key: 'opening_hours_sunday', label: language === 'pt' ? 'Horário (Domingo)' : 'Hours (Sunday)', type: 'text', placeholder: '10:00-22:00' },
          { key: 'is_open', label: language === 'pt' ? 'Aberto Agora' : 'Open Now', type: 'switch' },
          { key: 'is_out_of_stock', label: language === 'pt' ? 'Esgotado/Indisponível' : 'Out of Stock', type: 'switch' },
          { key: 'is_active', label: 'Ativo', type: 'switch' }
        ];
      case 'menuItem':
        return [
          { key: 'name', label: 'Nome', type: 'text', required: true },
          { key: 'description', label: 'Descrição', type: 'textarea' },
          { key: 'price', label: 'Preço (€)', type: 'number', step: '0.10', required: true },
          { key: 'category', label: 'Categoria', type: 'text', required: true, placeholder: 'Pratos, Bebidas, Sobremesas, Entradas...' },
          { key: 'image', label: 'Imagem', type: 'file' },
          { key: 'is_popular', label: 'Popular', type: 'switch' },
          { key: 'is_available', label: 'Disponível', type: 'switch' }
        ];
      case 'driver':
        return [
          { key: 'name', label: 'Nome', type: 'text', required: true },
          { key: 'email', label: 'Email', type: 'email', required: true },
          { key: 'phone', label: 'Telefone', type: 'text', required: true },
          { key: 'password', label: 'Password (mínimo 6 caracteres)', type: 'password', required: true, placeholder: 'A senha que ele vai usar para fazer login' },
          { key: 'vehicle_type', label: 'Tipo de Veículo', type: 'select', options: ['scooter', 'car', 'bicycle'] },
          { key: 'photo_url', label: 'Foto de Perfil', type: 'file' },
          { key: 'is_active', label: 'Ativo', type: 'switch' },
          { key: 'is_available', label: 'Disponível', type: 'switch' }
        ];
      case 'shisha':
        return [
          { key: 'name', label: 'Nome', type: 'text', required: true },
          { key: 'type', label: 'Tipo', type: 'select', options: ['standard', 'premium'] },
          { key: 'base_price', label: 'Preço Base (€/hora)', type: 'number', step: '0.50', required: true },
          { key: 'deposit', label: 'Depósito (€)', type: 'number', step: '1.00' },
          { key: 'image', label: 'Imagem', type: 'file' },
          { key: 'is_available', label: 'Disponível', type: 'switch' }
        ];
      case 'flavor':
        return [
          { key: 'name', label: 'Nome', type: 'text', required: true },
          { key: 'price', label: 'Preço Extra (€)', type: 'number', step: '0.50' },
          { key: 'is_available', label: 'Disponível', type: 'switch' }
        ];
      case 'shisha_extra':
        return [
          { key: 'name', label: language === 'pt' ? 'Nome' : 'Name', type: 'text', required: true },
          { key: 'description', label: language === 'pt' ? 'Descrição' : 'Description', type: 'textarea' },
          { key: 'category', label: language === 'pt' ? 'Categoria' : 'Category', type: 'select', options: ['charcoal', 'hose', 'flavor', 'other'] },
          { key: 'price', label: language === 'pt' ? 'Preço (€)' : 'Price (€)', type: 'number', step: '0.50', required: true },
          { key: 'max_quantity', label: language === 'pt' ? 'Quantidade Máxima' : 'Max Quantity', type: 'number', step: '1' },
          { key: 'image', label: language === 'pt' ? 'Imagem' : 'Image', type: 'file' },
          { key: 'is_available', label: language === 'pt' ? 'Disponível' : 'Available', type: 'switch' }
        ];
      case 'zone':
        return [
          { key: 'name', label: 'Nome', type: 'text', required: true },
          { key: 'type', label: 'Tipo', type: 'select', options: ['hotel', 'apartment', 'beach', 'marina', 'residential'] },
          { key: 'delivery_fee', label: 'Taxa de Entrega (€)', type: 'number', step: '0.50', required: true },
          { key: 'is_active', label: 'Ativa', type: 'switch' }
        ];
      case 'ice':
        return [
          { key: 'name', label: 'Nome (EN)', type: 'text', required: true },
          { key: 'name_pt', label: 'Nome (PT)', type: 'text', required: true },
          { key: 'description', label: 'Descrição (EN)', type: 'textarea' },
          { key: 'description_pt', label: 'Descrição (PT)', type: 'textarea' },
          { key: 'price', label: 'Preço (€)', type: 'number', step: '0.50', required: true },
          { key: 'image', label: 'Imagem', type: 'file' },
          { key: 'is_available', label: 'Disponível', type: 'switch' }
        ];
      default:
        return [];
    }
  };

  const handleFormSave = (formData, isEdit) => {
    const { type, data } = editDialog;
    if (isEdit) {
      const idField = type === 'restaurant' ? 'restaurant_id' : type === 'menuItem' ? 'item_id' : type === 'driver' ? 'driver_id' : type === 'shisha' ? 'shisha_id' : type === 'flavor' ? 'flavor_id' : type === 'shisha_extra' ? 'extra_id' : type === 'zone' ? 'zone_id' : type === 'ice' ? 'product_id' : 'id';
      handleUpdate(type, data[idField], formData);
    } else {
      if (type === 'menuItem' && selectedRestaurant) {
        handleCreate(type, { ...formData, restaurant_id: selectedRestaurant.restaurant_id });
      } else {
        handleCreate(type, formData);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] p-8">
        <Skeleton className="h-12 w-48 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {Array(4).fill(0).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Delete order confirmation */}
      <Dialog open={!!deleteOrderTarget} onOpenChange={(o) => { if (!o) { setDeleteOrderTarget(null); setDeleteExclude(false); } }}>
        <DialogContent data-testid="delete-order-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#004E64]">
              <Trash2 size={18} className="text-red-600" />
              {language === 'pt' ? 'Eliminar pedido do sistema?' : 'Delete order from the system?'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#9FA2B4]">
              {language === 'pt'
                ? `O pedido #${deleteOrderTarget?.order_id?.slice(-8)} deixa de aparecer no POS e na lista de pedidos.`
                : `Order #${deleteOrderTarget?.order_id?.slice(-8)} will no longer appear in the POS or order list.`}
            </p>
            <div className="flex items-start gap-3 rounded-xl border border-[#004E64]/10 p-3">
              <Switch
                checked={deleteExclude}
                onCheckedChange={setDeleteExclude}
                data-testid="delete-exclude-switch"
              />
              <div>
                <p className="text-sm font-semibold text-[#004E64]">
                  {language === 'pt' ? 'Não contar no total da plataforma' : "Don't count in platform total"}
                </p>
                <p className="text-xs text-[#9FA2B4]">
                  {language === 'pt'
                    ? deleteExclude
                      ? 'O pedido NÃO aparecerá nos relatórios financeiros nem no relatório mensal.'
                      : 'Por defeito, o pedido continua a contar nos relatórios financeiros.'
                    : deleteExclude
                      ? 'The order will be excluded from financial and monthly reports.'
                      : 'By default, the order still counts in financial reports.'}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => { setDeleteOrderTarget(null); setDeleteExclude(false); }} data-testid="delete-order-cancel">
                {language === 'pt' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button className="bg-red-600 hover:bg-red-700 rounded-full" onClick={handleDeleteOrder} data-testid="delete-order-confirm">
                {language === 'pt' ? 'Eliminar' : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Header */}
      <div className="bg-[#004E64] text-white py-4 px-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-full" data-testid="back-home-btn">
                <Home size={20} />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold" data-testid="admin-title">
                {language === 'pt' ? 'Painel Admin' : 'Admin Dashboard'}
              </h1>
              <p className="text-white/70 text-sm">{user?.name}</p>
            </div>
          </div>
          {/* Quick links to other staff dashboards */}
          <div className="flex items-center gap-2">
            <Link to="/pos" data-testid="admin-quick-pos-link">
              <Button size="sm" className="bg-white/15 hover:bg-white/25 text-white rounded-full border border-white/30">
                <Monitor size={14} className="mr-1" />
                {language === 'pt' ? 'Web POS' : 'Web POS'}
              </Button>
            </Link>
            <Link to="/driver" data-testid="admin-quick-driver-link">
              <Button size="sm" variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 rounded-full">
                <Bike size={14} className="mr-1" />
                {language === 'pt' ? 'Painel Entregador' : 'Driver Panel'}
              </Button>
            </Link>
            <Link to="/restaurant-portal" data-testid="admin-quick-portal-link">
              <Button size="sm" variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 rounded-full">
                <Store size={14} className="mr-1" />
                {language === 'pt' ? 'Portal Restaurante' : 'Restaurant Portal'}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Pedidos Hoje' : 'Today Orders'}</p>
                  <p className="text-2xl font-bold text-[#004E64]">{stats?.today_orders || 0}</p>
                </div>
                <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-full flex items-center justify-center">
                  <Package className="text-[#FF6B35]" size={18} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Total Pedidos' : 'Total Orders'}</p>
                  <p className="text-2xl font-bold text-[#004E64]">{stats?.total_orders || 0}</p>
                </div>
                <div className="w-10 h-10 bg-[#2EC4B6]/10 rounded-full flex items-center justify-center">
                  <TrendingUp className="text-[#2EC4B6]" size={18} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Pendentes' : 'Pending'}</p>
                  <p className="text-2xl font-bold text-[#004E64]">{stats?.pending_orders || 0}</p>
                </div>
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Clock className="text-yellow-600" size={18} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Faturamento' : 'Revenue'}</p>
                  <p className="text-2xl font-bold text-[#004E64]">€{stats?.total_revenue?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <DollarSign className="text-green-600" size={18} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto pb-2">
            <TabsList className="bg-white rounded-full p-1 shadow-sm mb-4 inline-flex min-w-max">
              <TabsTrigger value="dashboard" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <LayoutDashboard size={14} className="mr-1" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="orders" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <Package size={14} className="mr-1" />
                {language === 'pt' ? 'Pedidos' : 'Orders'}
              </TabsTrigger>
              <TabsTrigger value="dispatch" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3" data-testid="tab-dispatch">
                <Zap size={14} className="mr-1" />
                {language === 'pt' ? 'Despacho' : 'Dispatch'}
              </TabsTrigger>
              <TabsTrigger value="restaurants" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <Utensils size={14} className="mr-1" />
                {language === 'pt' ? 'Restaurantes' : 'Restaurants'}
              </TabsTrigger>
              <TabsTrigger value="shisha" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <Wind size={14} className="mr-1" />
                Shisha
              </TabsTrigger>
              <TabsTrigger value="ice" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <Snowflake size={14} className="mr-1" />
                {language === 'pt' ? 'Gelo' : 'Ice'}
              </TabsTrigger>
              <TabsTrigger value="drivers" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <Truck size={14} className="mr-1" />
                {language === 'pt' ? 'Entregadores' : 'Drivers'}
              </TabsTrigger>
              <TabsTrigger value="zones" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <MapPin size={14} className="mr-1" />
                {language === 'pt' ? 'Zonas' : 'Zones'}
              </TabsTrigger>
              <TabsTrigger value="payouts" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <DollarSign size={14} className="mr-1" />
                {language === 'pt' ? 'Pagamentos' : 'Payouts'}
              </TabsTrigger>
              <TabsTrigger value="receipts" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3" data-testid="tab-receipts">
                <Receipt size={14} className="mr-1" />
                {language === 'pt' ? 'Recibos' : 'Receipts'}
              </TabsTrigger>
              <TabsTrigger value="managers" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3" data-testid="tab-managers">
                <Store size={14} className="mr-1" />
                {language === 'pt' ? 'Gestores POS' : 'POS Managers'}
              </TabsTrigger>
              <TabsTrigger value="config" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3" data-testid="tab-platform-config">
                <Settings size={14} className="mr-1" />
                {language === 'pt' ? 'Entregas & Zona' : 'Delivery & Zone'}
              </TabsTrigger>
              <TabsTrigger value="analytics" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <BarChart3 size={14} className="mr-1" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="services" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <Grid size={14} className="mr-1" />
                {language === 'pt' ? 'Serviços' : 'Services'}
              </TabsTrigger>
              <TabsTrigger value="special-menus" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <Tag size={14} className="mr-1" />
                {language === 'pt' ? 'Menus Especiais' : 'Special Menus'}
              </TabsTrigger>
              <TabsTrigger value="alerts" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <Bell size={14} className="mr-1" />
                {language === 'pt' ? 'Alertas' : 'Alerts'}
              </TabsTrigger>
              <TabsTrigger value="promotions" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <Tag size={14} className="mr-1" />
                {language === 'pt' ? 'Promocoes' : 'Promotions'}
              </TabsTrigger>
              <TabsTrigger value="customer-analytics" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3" data-testid="tab-customer-analytics">
                <Users size={14} className="mr-1" />
                {language === 'pt' ? 'Clientes' : 'Customers'}
              </TabsTrigger>
              <TabsTrigger value="rest-zones" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <MapPin size={14} className="mr-1" />
                {language === 'pt' ? 'Zonas Rest.' : 'Rest. Zones'}
              </TabsTrigger>
              <TabsTrigger value="admins" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <UserCog size={14} className="mr-1" />
                {language === 'pt' ? 'Admins' : 'Admins'}
              </TabsTrigger>
              <TabsTrigger value="settings" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-3">
                <Settings size={14} className="mr-1" />
                {language === 'pt' ? 'Definições' : 'Settings'}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Recent Orders */}
              <Card className="rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>{language === 'pt' ? 'Pedidos Recentes' : 'Recent Orders'}</span>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab('orders')}>
                      {language === 'pt' ? 'Ver Todos' : 'View All'} <ChevronRight size={14} />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {orders.slice(0, 5).map(order => (
                      <div key={order.order_id} className="flex items-center justify-between p-2 bg-[#F4F1EA] rounded-xl">
                        <div>
                          <p className="font-semibold text-[#004E64] text-sm">#{order.order_id.slice(-8)}</p>
                          <p className="text-xs text-[#9FA2B4]">{order.customer_name}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={`${getStatusColor(order.status)} text-xs`}>
                            {getStatusLabel(order.status)}
                          </Badge>
                          <p className="text-xs font-bold text-[#FF6B35] mt-1">€{order.total.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Rentals */}
              <Card className="rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>{language === 'pt' ? 'Alugueres Recentes' : 'Recent Rentals'}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {rentals.slice(0, 5).map(rental => (
                      <div key={rental.rental_id} className="flex items-center justify-between p-2 bg-[#F4F1EA] rounded-xl">
                        <div>
                          <p className="font-semibold text-[#004E64] text-sm">{rental.shisha_name}</p>
                          <p className="text-xs text-[#9FA2B4]">{rental.customer_name}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={`${getStatusColor(rental.status)} text-xs`}>
                            {getStatusLabel(rental.status)}
                          </Badge>
                          <p className="text-xs font-bold text-[#FF6B35] mt-1">€{rental.total.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card className="rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{language === 'pt' ? 'Gerenciar Pedidos' : 'Manage Orders'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {orders.map(order => (
                    <div 
                      key={order.order_id} 
                      className="border border-[#004E64]/10 rounded-xl p-3 cursor-pointer hover:bg-[#F4F1EA]/50 transition-colors"
                      onClick={() => { setSelectedOrder(order); setOrderModalOpen(true); }}
                      data-testid={`order-card-${order.order_id}`}
                    >
                      <div className="flex flex-col gap-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-bold text-[#004E64] text-sm">#{order.order_id.slice(-8)}</span>
                              <Badge className={`${getStatusColor(order.status)} text-xs`}>
                                {getStatusLabel(order.status)}
                              </Badge>
                              {order.status === 'cancelled' ? (
                                <Badge className="bg-red-100 text-red-700 text-xs">
                                  {order.payment_status === 'refunded'
                                    ? (language === 'pt' ? 'Reembolsado' : 'Refunded')
                                    : (language === 'pt' ? 'Cancelado' : 'Cancelled')}
                                </Badge>
                              ) : order.payment_status === 'paid' ? (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  {language === 'pt' ? 'Pago' : 'Paid'}
                                </Badge>
                              ) : (
                                <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                                  {language === 'pt' ? 'Pendente' : 'Pending'}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-[#9FA2B4]">
                              <strong>{order.customer_name}</strong> • {order.phone}
                            </p>
                            <p className="text-xs text-[#9FA2B4]">
                              <MapPin size={10} className="inline mr-1" />
                              {order.delivery_location}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-[#FF6B35]">€{order.total?.toFixed(2)}</p>
                            <div className="flex items-center justify-end gap-1 mt-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-xs"
                                onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); setOrderModalOpen(true); }}
                              >
                                <Eye size={14} className="mr-1" />
                                {language === 'pt' ? 'Detalhes' : 'Details'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={(e) => { e.stopPropagation(); setDeleteExclude(false); setDeleteOrderTarget(order); }}
                                data-testid={`delete-order-${order.order_id}`}
                              >
                                <Trash2 size={14} className="mr-1" />
                                {language === 'pt' ? 'Eliminar' : 'Delete'}
                              </Button>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          {['delivered', 'cancelled'].includes(order.status) ? (
                            <p className="flex-1 text-xs text-[#9FA2B4] italic py-1.5">
                              {language === 'pt'
                                ? `Pedido ${getStatusLabel(order.status).toLowerCase()} — não editável`
                                : `Order ${order.status} — locked`}
                            </p>
                          ) : (
                          <>
                          <Select
                            value={order.status}
                            onValueChange={(value) => updateOrderStatus(order.order_id, value)}
                          >
                            <SelectTrigger className="flex-1 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {statusOptions.map(status => (
                                <SelectItem key={status.value} value={status.value}>
                                  {status.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={order.driver_id || ''}
                            onValueChange={(value) => updateOrderStatus(order.order_id, order.status, value)}
                          >
                            <SelectTrigger className="flex-1 h-8 text-xs">
                              <SelectValue placeholder={language === 'pt' ? 'Entregador' : 'Driver'} />
                            </SelectTrigger>
                            <SelectContent>
                              {drivers.map(driver => (
                                <SelectItem key={driver.driver_id} value={driver.driver_id}>
                                  {driver.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Order Details Modal */}
            <OrderDetailsModal 
              order={selectedOrder}
              open={orderModalOpen}
              onOpenChange={setOrderModalOpen}
              token={token}
              onUpdate={() => fetchData()}
            />
          </TabsContent>

          {/* Rental Details Modal (shisha) */}
          <RentalDetailsModal
            rental={selectedRental}
            open={!!selectedRental}
            onOpenChange={(o) => { if (!o) setSelectedRental(null); }}
            token={token}
            language={language}
            onUpdate={() => fetchData()}
          />

          {/* Dispatch Tab */}
          <TabsContent value="dispatch">
            <DispatchPanel token={token} language={language} drivers={drivers} orders={orders} rentals={rentals} onRefresh={fetchData} />
          </TabsContent>

          {/* Restaurants Tab */}
          <TabsContent value="restaurants">
            <Card className="rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>{selectedRestaurant ? `Menu: ${selectedRestaurant.name}` : (language === 'pt' ? 'Restaurantes' : 'Restaurants')}</span>
                  {selectedRestaurant ? (
                    <Button variant="ghost" size="sm" onClick={() => setSelectedRestaurant(null)}>
                      <ArrowLeft size={14} className="mr-1" /> {language === 'pt' ? 'Voltar' : 'Back'}
                    </Button>
                  ) : (
                    <Button 
                      size="sm" 
                      className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
                      onClick={() => {
                        setEditDialog({ open: true, type: 'restaurant', data: null });
                      }}
                    >
                      <Plus size={14} className="mr-1" /> {language === 'pt' ? 'Adicionar' : 'Add'}
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedRestaurant ? (
                  // Menu Items View
                  <div className="space-y-3">
                    <Button 
                      size="sm" 
                      className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full mb-3"
                      onClick={() => {
                        setEditDialog({ open: true, type: 'menuItem', data: null });
                      }}
                    >
                      <Plus size={14} className="mr-1" /> {language === 'pt' ? 'Adicionar Prato' : 'Add Item'}
                    </Button>
                    {menuItems.map(item => (
                      <div key={item.item_id} className="flex items-center justify-between p-3 border border-[#004E64]/10 rounded-xl">
                        <div className="flex items-center gap-3">
                          {item.image && (
                            <img src={item.image} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-[#004E64] text-sm">{item.name}</p>
                              {item.is_popular && <Badge className="bg-[#FF6B35] text-xs">Popular</Badge>}
                              {!item.is_available && <Badge variant="outline" className="text-xs">Indisponível</Badge>}
                            </div>
                            <p className="text-xs text-[#9FA2B4]">{item.category}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-[#FF6B35]">€{item.price.toFixed(2)}</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => {
                              setEditDialog({ open: true, type: 'menuItem', data: item });
                            }}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-red-500"
                            onClick={() => handleDelete('menuItem', item.item_id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Restaurants List
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {restaurants.map(restaurant => (
                      <div key={restaurant.restaurant_id} className="border border-[#004E64]/10 rounded-xl p-3">
                        <div className="flex items-center gap-3 mb-2">
                          <img 
                            src={restaurant.logo} 
                            alt={restaurant.name}
                            className="w-12 h-12 rounded-xl object-cover"
                          />
                          <div className="flex-1">
                            <p className="font-bold text-[#004E64] text-sm">{restaurant.name}</p>
                            <p className="text-xs text-[#9FA2B4]">{restaurant.cuisine_type}</p>
                          </div>
                          <Switch 
                            checked={restaurant.is_active} 
                            onCheckedChange={(checked) => handleUpdate('restaurant', restaurant.restaurant_id, { is_active: checked })}
                          />
                        </div>
                        <div className="flex items-center justify-between text-xs text-[#9FA2B4] mb-2">
                          <span>⭐ {restaurant.rating}</span>
                          <span>{restaurant.delivery_time}</span>
                          <span>€{restaurant.delivery_fee}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1 h-8 text-xs"
                            onClick={() => {
                              setSelectedRestaurant(restaurant);
                              fetchMenuItems(restaurant.restaurant_id);
                            }}
                          >
                            <Eye size={12} className="mr-1" /> Menu
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-8"
                            onClick={() => {
                              setHoursDialog({ open: true, restaurant });
                            }}
                            data-testid={`restaurant-hours-btn-${restaurant.restaurant_id}`}
                          >
                            <Clock size={12} />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-8"
                            onClick={() => {
                              setEditDialog({ open: true, type: 'restaurant', data: restaurant });
                            }}
                          >
                            <Edit size={12} />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-8 text-red-500"
                            onClick={() => handleDelete('restaurant', restaurant.restaurant_id)}
                          >
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shisha Tab */}
          <TabsContent value="shisha">
            <div className="space-y-6">
              {/* Shisha Extras / Add-ons (Carvão, Mangueira, Sabor extra, etc.) */}
              <Card className="rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>{language === 'pt' ? 'Extras da Shisha (Carvão, Mangueira, Sabor extra...)' : 'Shisha Extras (Charcoal, Hose, Extra Flavor...)'}</span>
                    <Button 
                      size="sm" 
                      className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
                      onClick={() => { setEditDialog({ open: true, type: 'shisha_extra', data: null }); }}
                      data-testid="add-shisha-extra-btn"
                    >
                      <Plus size={14} className="mr-1" /> {language === 'pt' ? 'Novo Extra' : 'New Extra'}
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {shishaExtras.length === 0 ? (
                    <p className="text-sm text-[#9FA2B4] text-center py-4">{language === 'pt' ? 'Sem extras. Crie o primeiro!' : 'No extras yet. Create one!'}</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {shishaExtras.map(extra => (
                        <div key={extra.extra_id} className="flex items-center justify-between p-3 border border-[#004E64]/10 rounded-xl" data-testid={`shisha-extra-${extra.extra_id}`}>
                          <div className="flex items-center gap-3">
                            {extra.image && <img src={extra.image.startsWith('http') ? extra.image : `${API_URL}${extra.image}`} alt={extra.name} className="w-12 h-12 rounded-lg object-cover" />}
                            <div>
                              <p className="font-semibold text-[#004E64] text-sm">{extra.name}</p>
                              {extra.description && <p className="text-xs text-[#9FA2B4]">{extra.description}</p>}
                              <Badge className="bg-purple-100 text-purple-700 text-xs mt-1">{extra.category || 'other'}</Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-[#FF6B35]">{'\u20AC'}{extra.price?.toFixed(2)}</span>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditDialog({ open: true, type: 'shisha_extra', data: extra }); }}>
                              <Edit size={12} />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete('shisha_extra', extra.extra_id)}>
                              <Trash2 size={12} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Shisha Rentals List */}
              <Card className="rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{language === 'pt' ? 'Alugueres de Shisha' : 'Shisha Rentals'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {rentals.map(rental => (
                      <div key={rental.rental_id} className="border border-[#004E64]/10 rounded-xl p-4 hover:border-[#FF6B35]/30 transition-colors cursor-pointer" onClick={() => setSelectedRental(rental)} data-testid={`rental-${rental.rental_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                              <Wind size={18} className="text-purple-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-[#004E64] text-sm">#{rental.rental_id?.slice(-8)}</p>
                              <p className="text-xs text-[#9FA2B4]">{rental.customer_name} • {rental.phone}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className={`${getStatusColor(rental.status)} text-xs`}>
                              {getStatusLabel(rental.status)}
                            </Badge>
                            <p className="text-sm font-bold text-[#FF6B35] mt-1">{'\u20AC'}{rental.total?.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Shisha Models & Flavors */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Shisha Models */}
              <Card className="rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>{language === 'pt' ? 'Modelos de Shisha' : 'Shisha Models'}</span>
                    <Button 
                      size="sm" 
                      className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
                      onClick={() => {
                        setEditDialog({ open: true, type: 'shisha', data: null });
                      }}
                    >
                      <Plus size={14} className="mr-1" /> Add
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {shishas.map(shisha => (
                      <div key={shisha.shisha_id} className="flex items-center justify-between p-2 border border-[#004E64]/10 rounded-xl">
                        <div className="flex items-center gap-2">
                          {shisha.image && <img src={shisha.image} alt={shisha.name} className="w-10 h-10 rounded-lg object-cover" />}
                          <div>
                            <p className="font-semibold text-[#004E64] text-sm">{shisha.name}</p>
                            <p className="text-xs text-[#9FA2B4]">{shisha.type} • €{shisha.base_price}/h</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditDialog({ open: true, type: 'shisha', data: shisha }); }}>
                            <Edit size={12} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete('shisha', shisha.shisha_id)}>
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Flavors */}
              <Card className="rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>{language === 'pt' ? 'Sabores' : 'Flavors'}</span>
                    <Button 
                      size="sm" 
                      className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
                      onClick={() => {
                        setEditDialog({ open: true, type: 'flavor', data: null });
                      }}
                    >
                      <Plus size={14} className="mr-1" /> Add
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {flavors.map(flavor => (
                      <div key={flavor.flavor_id} className="flex items-center justify-between p-2 border border-[#004E64]/10 rounded-xl">
                        <div>
                          <p className="font-semibold text-[#004E64] text-sm">{flavor.name}</p>
                          {flavor.price > 0 && <p className="text-xs text-[#9FA2B4]">+€{flavor.price}</p>}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditDialog({ open: true, type: 'flavor', data: flavor }); }}>
                            <Edit size={12} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete('flavor', flavor.flavor_id)}>
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
            </div>
          </TabsContent>

          {/* Ice Tab */}
          <TabsContent value="ice">
            <Card className="rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>{language === 'pt' ? 'Produtos de Gelo' : 'Ice Products'}</span>
                  <Button 
                    size="sm" 
                    className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
                    onClick={() => {
                      setEditDialog({ open: true, type: 'ice', data: null });
                    }}
                  >
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {iceProducts.map(product => (
                    <div key={product.product_id} className="flex items-center justify-between p-3 border border-[#004E64]/10 rounded-xl">
                      <div className="flex items-center gap-3">
                        {product.image && <img src={product.image} alt={product.name} className="w-12 h-12 rounded-lg object-cover" />}
                        <div>
                          <p className="font-semibold text-[#004E64] text-sm">{language === 'pt' ? product.name_pt : product.name}</p>
                          <p className="text-xs text-[#9FA2B4]">{language === 'pt' ? product.description_pt : product.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[#FF6B35]">€{product.price.toFixed(2)}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditDialog({ open: true, type: 'ice', data: product }); }}>
                          <Edit size={12} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete('ice', product.product_id)}>
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Drivers Tab */}
          <TabsContent value="drivers">
            <Card className="rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>{language === 'pt' ? 'Entregadores' : 'Drivers'}</span>
                  <Button 
                    size="sm" 
                    className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
                    onClick={() => {
                      setEditDialog({ open: true, type: 'driver', data: null });
                    }}
                  >
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {drivers.map(driver => (
                    <div key={driver.driver_id} className="border border-[#004E64]/10 rounded-xl p-3">
                      <div className="flex items-center gap-3 mb-2">
                        {driver.photo_url ? (
                          <img src={driver.photo_url} alt={driver.name} className="w-10 h-10 rounded-full object-cover" data-testid={`driver-photo-${driver.driver_id}`} />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[#FF6B35] flex items-center justify-center text-white font-bold text-sm">
                            {driver.name.charAt(0)}
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-bold text-[#004E64] text-sm">{driver.name}</p>
                          <p className="text-xs text-[#9FA2B4]">{driver.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs mb-2">
                        <Badge className={driver.is_available ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {driver.is_available ? (language === 'pt' ? 'Disponível' : 'Available') : (language === 'pt' ? 'Ocupado' : 'Busy')}
                        </Badge>
                        <span className="text-[#9FA2B4]">{driver.total_deliveries} {language === 'pt' ? 'entregas' : 'deliveries'}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex-1 h-7 text-xs" onClick={() => { setEditDialog({ open: true, type: 'driver', data: driver }); }}>
                          <Edit size={12} className="mr-1" /> {language === 'pt' ? 'Editar' : 'Edit'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={async () => {
                            const newPwd = window.prompt(language === 'pt' ? 'Nova password (mínimo 6 caracteres):' : 'New password (min 6 chars):');
                            if (!newPwd) return;
                            if (newPwd.length < 6) {
                              toast.error(language === 'pt' ? 'Mínimo 6 caracteres' : 'Min 6 chars');
                              return;
                            }
                            const r = await fetch(`${API_URL}/api/admin/drivers/${driver.driver_id}/reset-password`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ password: newPwd }),
                            });
                            if (r.ok) toast.success(language === 'pt' ? 'Password atualizada' : 'Password updated');
                            else toast.error((await r.json()).detail || 'Erro');
                          }}
                          data-testid={`reset-driver-pwd-${driver.driver_id}`}
                        >
                          🔑
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs text-red-500" onClick={() => handleDelete('driver', driver.driver_id)}>
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Zones Tab */}
          <TabsContent value="zones">
            <Card className="rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>{language === 'pt' ? 'Zonas de Entrega' : 'Delivery Zones'}</span>
                  <Button 
                    size="sm" 
                    className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
                    onClick={() => {
                      setEditDialog({ open: true, type: 'zone', data: null });
                    }}
                  >
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {deliveryZones.map(zone => (
                    <div key={zone.zone_id} className="flex items-center justify-between p-3 border border-[#004E64]/10 rounded-xl">
                      <div>
                        <p className="font-semibold text-[#004E64] text-sm">{zone.name}</p>
                        <p className="text-xs text-[#9FA2B4]">{zone.type} • €{zone.delivery_fee}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Switch 
                          checked={zone.is_active} 
                          onCheckedChange={(checked) => handleUpdate('zone', zone.zone_id, { is_active: checked })}
                        />
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditDialog({ open: true, type: 'zone', data: zone }); }}>
                          <Edit size={12} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete('zone', zone.zone_id)}>
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts">
            <PayoutManagement />
          </TabsContent>

          {/* Restaurant Managers (POS) Tab */}
          <TabsContent value="managers">
            <RestaurantManagersTab token={token} />
          </TabsContent>

          {/* Platform Config Tab (delivery pricing + platform hours) */}
          <TabsContent value="config">
            <PlatformConfigTab token={token} />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AdvancedAnalytics />
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <ServiceManagement />
          </TabsContent>

          {/* Special Menus Tab */}
          <TabsContent value="special-menus">
            <SpecialMenusManagement />
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <AdminAlertsPanel />
          </TabsContent>

          {/* Promotions Tab */}
          <TabsContent value="promotions">
            <PromotionsManagement token={token} language={language} />
          </TabsContent>

          {/* Customer & Delivery Analytics Tab */}
          {/* Receipts Tab */}
          <TabsContent value="receipts">
            <ReceiptsManager token={token} language={language} />
          </TabsContent>

          <TabsContent value="customer-analytics">
            <div className="space-y-6">
              <CustomersManager token={token} language={language} />
              <CustomerDeliveryAnalytics token={token} language={language} />
            </div>
          </TabsContent>

          {/* Restaurant Zones Tab */}
          <TabsContent value="rest-zones">
            <RestaurantZonesManager token={token} language={language} restaurants={restaurants} zones={deliveryZones} />
          </TabsContent>

          {/* Admins Tab */}
          <TabsContent value="admins">
            <AdminManagement />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            {settings && (
              <Card className="rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{language === 'pt' ? 'Configurações da Plataforma' : 'Platform Settings'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-[#004E64] mb-3 text-sm">
                      {language === 'pt' ? 'Mensagens de Pagamento' : 'Payment Messages'}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-xs text-[#9FA2B4]">MBWay / Apple Pay Message</label>
                        <Textarea
                          value={settings.mbway_message}
                          onChange={(e) => setSettings({ ...settings, mbway_message: e.target.value, apple_pay_message: e.target.value })}
                          className="mt-1 rounded-xl text-sm"
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-[#004E64] mb-3 text-sm">
                      {language === 'pt' ? 'Taxas de Entrega' : 'Delivery Fees'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Taxa Padrão (€)' : 'Standard Fee (€)'}</label>
                        <Input
                          type="number"
                          step="0.50"
                          value={settings.standard_delivery_fee}
                          onChange={(e) => setSettings({ ...settings, standard_delivery_fee: parseFloat(e.target.value) })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Taxa Praia (€)' : 'Beach Fee (€)'}</label>
                        <Input
                          type="number"
                          step="0.50"
                          value={settings.beach_delivery_fee}
                          onChange={(e) => setSettings({ ...settings, beach_delivery_fee: parseFloat(e.target.value) })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Taxa Premium (€)' : 'Premium Fee (€)'}</label>
                        <Input
                          type="number"
                          step="0.50"
                          value={settings.premium_delivery_fee}
                          onChange={(e) => setSettings({ ...settings, premium_delivery_fee: parseFloat(e.target.value) })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-[#004E64] mb-3 text-sm">
                      {language === 'pt' ? 'Extras Shisha (€)' : 'Shisha Extras (€)'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Carvão Extra' : 'Extra Charcoal'}</label>
                        <Input
                          type="number"
                          step="0.50"
                          value={settings.charcoal_price}
                          onChange={(e) => setSettings({ ...settings, charcoal_price: parseFloat(e.target.value) })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Mangueira Extra' : 'Extra Hose'}</label>
                        <Input
                          type="number"
                          step="0.50"
                          value={settings.extra_hose_price}
                          onChange={(e) => setSettings({ ...settings, extra_hose_price: parseFloat(e.target.value) })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Global Operating Hours */}
                  <div>
                    <h3 className="font-semibold text-[#004E64] mb-3 text-sm">
                      {language === 'pt' ? 'Horário de Funcionamento da Plataforma' : 'Platform Operating Hours'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Abre às' : 'Opens at'}</label>
                        <Input
                          type="time"
                          value={settings.site_open_time || '08:00'}
                          onChange={(e) => setSettings({ ...settings, site_open_time: e.target.value })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Fecha às' : 'Closes at'}</label>
                        <Input
                          type="time"
                          value={settings.site_close_time || '23:00'}
                          onChange={(e) => setSettings({ ...settings, site_close_time: e.target.value })}
                          className="mt-1 rounded-xl"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-[#F4F1EA] rounded-xl">
                      <div>
                        <p className="font-medium text-[#004E64] text-sm">
                          {language === 'pt' ? 'Aceitar Pedidos' : 'Accept Orders'}
                        </p>
                        <p className="text-xs text-[#9FA2B4]">
                          {language === 'pt' ? 'Desative para fechar temporariamente' : 'Turn off to temporarily close'}
                        </p>
                      </div>
                      <Switch
                        checked={settings.accepting_orders !== false}
                        onCheckedChange={(checked) => setSettings({ ...settings, accepting_orders: checked })}
                      />
                    </div>
                    {!settings.accepting_orders && (
                      <div className="mt-4 space-y-2">
                        <div>
                          <label className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Mensagem (PT)' : 'Message (PT)'}</label>
                          <Textarea
                            value={settings.closed_message || ''}
                            onChange={(e) => setSettings({ ...settings, closed_message: e.target.value })}
                            className="mt-1 rounded-xl text-sm"
                            rows={2}
                            placeholder="De momento não estamos a aceitar pedidos..."
                          />
                        </div>
                        <div>
                          <label className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Mensagem (EN)' : 'Message (EN)'}</label>
                          <Textarea
                            value={settings.closed_message_en || ''}
                            onChange={(e) => setSettings({ ...settings, closed_message_en: e.target.value })}
                            className="mt-1 rounded-xl text-sm"
                            rows={2}
                            placeholder="We are currently not accepting orders..."
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={updateSettings}
                    className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
                  >
                    <Save size={16} className="mr-2" />
                    {language === 'pt' ? 'Salvar Configurações' : 'Save Settings'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <EditFormDialog
        open={editDialog.open}
        type={editDialog.type}
        data={editDialog.data}
        fields={getFormFields(editDialog.type)}
        onClose={() => setEditDialog({ open: false, type: '', data: null })}
        onSave={handleFormSave}
        language={language}
        token={token}
      />

      <Dialog open={hoursDialog.open} onOpenChange={(o) => setHoursDialog({ open: o, restaurant: o ? hoursDialog.restaurant : null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="restaurant-hours-dialog">
          <DialogHeader>
            <DialogTitle>
              {language === 'pt' ? 'Horários' : 'Hours'}{hoursDialog.restaurant ? ` — ${hoursDialog.restaurant.name}` : ''}
            </DialogTitle>
          </DialogHeader>
          {hoursDialog.restaurant && (
            <RestaurantHoursEditor
              token={token}
              restaurant={hoursDialog.restaurant}
              onSaved={() => { setHoursDialog({ open: false, restaurant: null }); fetchData(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
